import * as React from "react";

import { UitkCard, UitkCardContentSection, UitkCardLink } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkRating } from "uitk-react-rating";
import { UitkLayoutFlex } from "uitk-react-layout-flex";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { UitkPriceLockupAlign } from "uitk-react-price-lockup";
import { useLocalization } from "@shared-ui/localization-context";

import PriceLockup from "components/flexComponents/Hotels/views/HotelsViews/components/shared/PriceLockup";
import ValueProposition from "components/flexComponents/Hotels/views/HotelsViews/components/shared/ValueProposition";
import DistanceOrRating from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DistanceOrRating";
import DescriptionOrReview from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DescriptionOrReview";
import AmenitiesOrRecommendation from "components/flexComponents/Hotels/views/HotelsViews/components/shared/AmenitiesOrRecommendation";
import { getHotelLink, getHotelLinkRel } from "components/flexComponents/Hotels/components/HotelCard";

import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { ratingIcon } from "components/utility/RatingUtil";
import { TrackedLink } from "components/utility/analytics/TrackedLink";

import { HotelImageCardProps } from "components/flexComponents/Hotels/typings";
import { getOptimizedImageUrl } from "src/components/flexComponents/Hotels/utils/hotelOptimizedImage";
import { HotelPriceLockup } from "src/components/flexComponents/Hotels/utils/A11y/HotelPriceLockup";

const HotelCard = (props: HotelImageCardProps) => {
  const { hotel, model, context } = props;
  const { callToAction, destinationId } = model;
  const { hotelName, hotelStarRating, hotelFormattedFullAddress, payLater, freeCancellation } = hotel;

  const hotelImageUrl = getOptimizedImageUrl(hotel, context);

  const showValueProposition = payLater || freeCancellation;
  const { formatText } = useLocalization();

  return (
    <UitkCard>
      <UitkCardContentSection padded={false}>
        <UitkLayoutGrid columns={2}>
          <UitkFigure ratio={UitkFigureAspectRatioType.R1_1}>
            <LazyLoadedImage src={hotelImageUrl} alt={hotel.hotelName} context={context} />
          </UitkFigure>
          <UitkLayoutFlex direction="column" className="all-x-padding-two all-y-padding-two">
            <UitkHeading tag="h3" size={5} overflow="truncate-2-lines">
              {hotelName}
            </UitkHeading>
            <UitkRating rating={hotelStarRating} icon={ratingIcon(context.locale)} className={context.site.brand} />
            <UitkText size={300} className="truncate-lines-2">
              {hotelFormattedFullAddress}
            </UitkText>
            <PriceLockup
              hotel={hotel}
              align={UitkPriceLockupAlign.LEFT}
              className="all-t-margin-three"
              priceA11y={new HotelPriceLockup(hotel, formatText)}
              hasGetRatesText
            />
          </UitkLayoutFlex>
        </UitkLayoutGrid>
      </UitkCardContentSection>

      <UitkCardContentSection border="top">
        <UitkLayoutFlex direction="column">
          <DistanceOrRating model={model} hotel={hotel} className="all-b-margin-three" context={context} />
          <DescriptionOrReview model={model} hotel={hotel} />
          <AmenitiesOrRecommendation model={model} hotel={hotel} className="all-t-margin-three" />
        </UitkLayoutFlex>
      </UitkCardContentSection>

      {showValueProposition && (
        <UitkCardContentSection border="top">
          <ValueProposition hotel={hotel} />
        </UitkCardContentSection>
      )}

      <UitkCardLink>
        <TrackedLink
          data-testid={`hotelcard-link-${hotel.hotelName}`}
          aria-label={hotelName}
          moduleName="hotels"
          rfrr="card"
          rel={getHotelLinkRel(callToAction)}
          href={getHotelLink(hotel, destinationId || context.searchContext.location?.id, context, callToAction)}
        />
      </UitkCardLink>
    </UitkCard>
  );
};

export default HotelCard;
